import type { FabricCanvasData, Project, ProjectPage } from '@canvas/shared-types';

export function createPageId(): string {
  return `page_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function blankCanvasData(width: number, height: number): FabricCanvasData {
  return {
    version: '5.3.0',
    objects: [],
    background: '#ffffff',
    width,
    height,
  };
}

export function createBlankPage(
  width: number,
  height: number,
  name?: string,
  index = 1
): ProjectPage {
  return {
    pageId: createPageId(),
    name: name || `Page ${index}`,
    canvasData: blankCanvasData(width, height),
  };
}

export function canvasObjectCount(data?: FabricCanvasData | null): number {
  return data?.objects?.length ?? 0;
}

/** Prefer the candidate with the most objects (heals empty pages vs full canvasData). */
export function pickRichestCanvasData(
  ...candidates: Array<FabricCanvasData | null | undefined>
): FabricCanvasData | null {
  let best: FabricCanvasData | null = null;
  let bestCount = -1;
  for (const candidate of candidates) {
    if (!candidate) continue;
    const count = canvasObjectCount(candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

/** Ensure a project has a pages array; migrate legacy single-canvas projects. */
export function normalizeProjectPages(project: Project): {
  pages: ProjectPage[];
  activePageId: string;
} {
  if (project.pages && project.pages.length > 0) {
    const activePageId =
      project.activePageId &&
      project.pages.some((p) => p.pageId === project.activePageId)
        ? project.activePageId
        : project.pages[0].pageId;
    return { pages: project.pages, activePageId };
  }

  const page: ProjectPage = {
    pageId: createPageId(),
    name: 'Page 1',
    canvasData: project.canvasData || blankCanvasData(project.width, project.height),
  };
  return { pages: [page], activePageId: page.pageId };
}

/**
 * Resolve pages + active canvas for the editor.
 * If top-level `canvasData` is richer than the active page (or the reverse),
 * heal so reopen does not render a blank artboard.
 */
export function resolveProjectForEditor(project: Project): {
  pages: ProjectPage[];
  activePageId: string;
  canvasData: FabricCanvasData;
} {
  const normalized = normalizeProjectPages(project);
  const active = getActivePage(normalized.pages, normalized.activePageId);
  const canvasData =
    pickRichestCanvasData(active?.canvasData, project.canvasData) ||
    blankCanvasData(project.width, project.height);

  const pages = replacePageData(
    normalized.pages,
    normalized.activePageId,
    canvasData
  );

  return {
    pages,
    activePageId: normalized.activePageId,
    canvasData,
  };
}

/**
 * Before save: keep `pages[active]` and top-level `canvasData` identical.
 */
export function ensurePagesMatchCanvas(
  project: Project,
  canvasData: FabricCanvasData
): { pages: ProjectPage[]; activePageId: string } {
  if (project.pages && project.pages.length > 0) {
    const activePageId =
      project.activePageId &&
      project.pages.some((p) => p.pageId === project.activePageId)
        ? project.activePageId
        : project.pages[0].pageId;
    return {
      pages: replacePageData(project.pages, activePageId, canvasData),
      activePageId,
    };
  }
  return normalizeProjectPages({ ...project, canvasData });
}

export function getActivePage(
  pages: ProjectPage[],
  activePageId: string
): ProjectPage | undefined {
  return pages.find((p) => p.pageId === activePageId) || pages[0];
}

export function replacePageData(
  pages: ProjectPage[],
  pageId: string,
  canvasData: FabricCanvasData
): ProjectPage[] {
  return pages.map((p) => (p.pageId === pageId ? { ...p, canvasData } : p));
}

export function duplicatePage(page: ProjectPage): ProjectPage {
  return {
    pageId: createPageId(),
    name: `${page.name} copy`,
    canvasData: {
      ...page.canvasData,
      objects: JSON.parse(JSON.stringify(page.canvasData.objects || [])),
      background: page.canvasData.background,
      width: page.canvasData.width,
      height: page.canvasData.height,
      version: page.canvasData.version || '5.3.0',
    },
  };
}

/** Move a page by delta (-1 left, +1 right). Returns same array if no-op. */
export function movePage(
  pages: ProjectPage[],
  pageId: string,
  delta: number
): ProjectPage[] {
  const from = pages.findIndex((p) => p.pageId === pageId);
  if (from < 0) return pages;
  const to = from + delta;
  if (to < 0 || to >= pages.length) return pages;
  const next = pages.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** Reorder by dragging `fromId` onto `toId` (insert before target). */
export function reorderPage(
  pages: ProjectPage[],
  fromId: string,
  toId: string
): ProjectPage[] {
  if (fromId === toId) return pages;
  const from = pages.findIndex((p) => p.pageId === fromId);
  const to = pages.findIndex((p) => p.pageId === toId);
  if (from < 0 || to < 0) return pages;
  const next = pages.slice();
  const [item] = next.splice(from, 1);
  const insertAt = next.findIndex((p) => p.pageId === toId);
  next.splice(insertAt < 0 ? next.length : insertAt, 0, item);
  return next;
}
